import React from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import Close from '@material-ui/icons/Close';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/styles';

import auth from 'commons/auth';
import { fineUploader } from 'commons/uploader';
import { VIEWS, FILE_UPLOAD_STATUS } from 'commons/constants';
import { appActions } from 'commons/store/app';
import { fileActions } from 'commons/store/file';
import { mapActions, mapSelectors } from 'commons/store/map';

import FilesProgress from './FilesProgress';
import TemporaryLayers from './TemporaryLayers';
import FileUploaderForm from './FileUploaderForm';

import styles from './styles';

const useStyles = makeStyles(styles);

const { insertNode } = fileActions;
const { getRecentlyCreatedLayers } = mapActions;
const { hideUploadsDrawer, showUploadsDrawer } = appActions;

export function UploadsDrawer() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [, setState] = React.useState();

  const maps = useSelector(mapSelectors.getRecentlyLayersSelector);
  const { open = false, view = '', node = {} } = useSelector(
    ({ app: { uploads } }) => uploads
  );

  const upload = React.useRef({
    uploader: fineUploader(auth.getHeaders()),
    submitted: {},
    uploaded: [],
  });

  React.useEffect(() => {
    upload.current.uploader.on('error', handleUploadError);
    upload.current.uploader.on('complete', handleUploadComplete);
    upload.current.uploader.on('progress', handleUploadProgress);

    dispatch(getRecentlyCreatedLayers());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    return () => {
      upload.current = {
        submitted: {},
        uploaded: [],
      };
    };
  }, []);

  const forceUpdate = React.useCallback(() => setState({}), []);

  const allSubmittedFiles = Object.values(upload.current.submitted).reduce(
    (acc, value) => [...acc, ...value.files],
    []
  );

  const findNodeByFileId = id =>
    Object.values(upload.current.submitted).find(item =>
      item.files.some(file => file.id === id)
    );

  const updateFilesStatus = (id, file) => {
    const { SUCCESS, ERROR } = FILE_UPLOAD_STATUS;

    const savedNode = findNodeByFileId(id);
    const status = file.success ? SUCCESS : ERROR;

    upload.current.submitted[savedNode.node.id] = {
      ...savedNode,
      files: savedNode.files.map(item =>
        item.id === id ? { ...item, status } : item
      ),
    };

    forceUpdate();
  };

  const handleHideDrawer = React.useCallback(
    () => dispatch(hideUploadsDrawer()),
    [dispatch]
  );

  const handleLayerRefresh = React.useCallback(
    () => dispatch(getRecentlyCreatedLayers()),
    [dispatch]
  );

  const handleUploadProgress = (id, name, byte, size) => {
    const savedNode = findNodeByFileId(id);
    const progress = Math.ceil((byte * 100) / size);

    upload.current.submitted[savedNode.node.id] = {
      ...savedNode,
      files: savedNode.files.map(item =>
        item.id === id ? { ...item, progress } : item
      ),
    };

    forceUpdate();
  };

  const handleCancelUpload = React.useCallback(
    submittedFiles => {
      submittedFiles.forEach(file =>
        upload.current.uploader.methods.cancel(file.id)
      );
      dispatch(hideUploadsDrawer());
    },
    [dispatch]
  );

  const handleUploadedFiles = React.useCallback(
    submittedFiles => {
      const _node = upload.current.submitted[node.id] || {};
      const files = _node.files || [];

      files.unshift(...submittedFiles);

      upload.current.submitted[node.id] = { node, files };
    },
    [node]
  );

  const handleUploadError = id => {
    const savedNode = findNodeByFileId(id);

    upload.current.submitted[savedNode.node.id] = {
      ...savedNode,
      files: savedNode.files.map(item =>
        item.id === id ? { ...item, status: FILE_UPLOAD_STATUS.ERROR } : item
      ),
    };

    forceUpdate();
  };

  const handleUploadComplete = (id, name, data, responseJSON) => {
    const response = !!responseJSON.response
      ? JSON.parse(responseJSON.response)
      : {};

    const notUploadedYet = upload.current.uploaded.indexOf(id) === -1;
    const savedNode = findNodeByFileId(id);

    if (!!response.id && !!savedNode && notUploadedYet) {
      dispatch(
        insertNode({
          parentId: savedNode.node.id,
          node: {
            name: data.name,
            parent: savedNode.node.id,
            data: {
              id: data.id,
              length: data.length,
              type: data.type,
              date: savedNode.node.date,
            },
          },
          silent: true,
        })
      );

      upload.current.uploaded.push(id);
    }

    updateFilesStatus(id, data);
    upload.current.uploader.methods.removeFileRef(id);
  };

  const handleUploadStoredFiles = React.useCallback(
    files => {
      upload.current.uploader.methods.uploadStoredFiles();
      handleUploadedFiles(files);
      dispatch(showUploadsDrawer({ view: VIEWS.UPLOAD_PROGRESS }));
    },
    [dispatch, handleUploadedFiles]
  );

  return (
    <Drawer anchor="right" open={open} onClose={handleHideDrawer} keepMounted>
      <div className={classes.header}>
        <Typography variant="h6">
          <FormattedMessage id="LABELS.COMMONS.UPLOADS" />
        </Typography>
        <IconButton className={classes.iconButton} onClick={handleHideDrawer}>
          <Close />
        </IconButton>
      </div>
      <div className={classes.content}>
        {view === VIEWS.UPLOAD_FILES && (
          <FileUploaderForm
            uploader={upload.current.uploader}
            node={node}
            onUploadStoredFiles={handleUploadStoredFiles}
            onCancel={handleCancelUpload}
          />
        )}
        {view === VIEWS.UPLOAD_PROGRESS && (
          <>
            <FilesProgress files={allSubmittedFiles} />
            <TemporaryLayers maps={maps} onLayerRefresh={handleLayerRefresh} />
          </>
        )}
      </div>
    </Drawer>
  );
}

export default UploadsDrawer;
